import React, { useCallback, useEffect, useState } from 'react';
import amplifyService from '../services/amplify.service.ts';
import { Button, Form, Spinner } from 'react-bootstrap';

type FeedbackProps = {
  user: any;
};

type FeedbackEntry = {
  id: string;
  content: string;
  createdBy?: string | null;
  createdAt?: string | null;
  name?: string | null;
  done?: boolean | null;
  upvotes?: number;
  downvotes?: number;
};

function Feedback({ user }: FeedbackProps) {
  const [feedbackItems, setFeedbackItems] = useState<FeedbackEntry[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [myVotes, setMyVotes] = useState<Record<string, number>>({});
  const [votingId, setVotingId] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resolveFeedbackDisplayName = () => {
    if (user?.attributes?.name) return user.attributes.name as string;
    if (user?.attributes?.email) {
      return (user.attributes.email as string).split('@')[0];
    }
    return user?.username || 'Anonymous';
  };

  const fetchFeedback = useCallback(async () => {
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      const items = await amplifyService.listFeedback();
      // Her feedback için createdBy'nin bir ID olup olmadığını kontrol et ve name'i çek
      const itemsWithNames = await Promise.all(
        items.map(async (item) => {
          // Eğer createdBy bir UUID formatındaysa (ID), kullanıcı bilgisini çek
          const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(item.createdBy || '');
          if (isUUID && item.createdBy) {
            try {
              const user = await amplifyService.getUser(item.createdBy);
              if (user && (user as any).name) {
                return { ...item, name: (user as any).name };
              }
            } catch (_err) {
              // Kullanıcı bulunamazsa, createdBy'yi olduğu gibi kullan
            }
          }
          // Eğer ID değilse veya name bulunamazsa, createdBy'yi olduğu gibi kullan
          return { ...item, name: item.createdBy };
        })
      );
      setFeedbackItems(itemsWithNames);
    } catch (_err) {
      setFeedbackError('Feedback could not be loaded. Please try again later.');
    } finally {
      setFeedbackLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  useEffect(() => {
    if (!user?.username) return;
    amplifyService.listMyFeedbackVotes(user.username).then(setMyVotes);
  }, [user?.username, feedbackItems.length]);



  const handleVote = async (feedbackId: string, value: 1 | -1) => {
    if (!user?.username) return;
    setVotingId(feedbackId);
    try {
      const result = await amplifyService.voteFeedback(feedbackId, user.username, value);
      if (result) {
        setFeedbackItems((prev) => {
          const updated = prev.map((item) =>
            item.id === feedbackId ? { ...item, upvotes: result.upvotes, downvotes: result.downvotes } : item
          );
          return updated.sort((a, b) => {
            const aDone = a.done ?? false;
            const bDone = b.done ?? false;
            if (aDone && !bDone) return 1;
            if (!aDone && bDone) return -1;
            const aScore = (a.upvotes ?? 0) - (a.downvotes ?? 0);
            const bScore = (b.upvotes ?? 0) - (b.downvotes ?? 0);
            if (bScore !== aScore) return bScore - aScore;
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bTime - aTime;
          });
        });
        setMyVotes((prev) => ({ ...prev, [feedbackId]: value }));
      }
    } finally {
      setVotingId(null);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) {
      setFeedbackError('Please add a short note before submitting.');
      return;
    }
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      const createdBy = resolveFeedbackDisplayName();
      const result = await amplifyService.addFeedback(feedbackText.trim(), createdBy);
      if (result) {
        // UUID kontrolü yap ve eğer UUID ise kullanıcı bilgisini çek
        let feedbackWithName: FeedbackEntry = result;
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(result.createdBy || '');
        if (isUUID && result.createdBy) {
          try {
            const userInfo = await amplifyService.getUser(result.createdBy);
            if (userInfo && (userInfo as any).name) {
              feedbackWithName = { ...result, name: (userInfo as any).name };
            } else {
              feedbackWithName = { ...result, name: result.createdBy };
            }
          } catch (_err) {
            feedbackWithName = { ...result, name: result.createdBy };
          }
        } else {
          feedbackWithName = { ...result, name: result.createdBy };
        }

        setFeedbackItems((prev) => {
          const next = [feedbackWithName, ...prev];
          // Remove duplicates by id if any
          const seen = new Set<string>();
          return next.filter((entry) => {
            if (seen.has(entry.id)) return false;
            seen.add(entry.id);
            return true;
          });
        });
        setFeedbackText('');
      } else {
        setFeedbackError('Feedback could not be saved. Please try again.');
      }
    } catch (_err) {
      setFeedbackError('Feedback could not be saved. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: '980px', paddingBottom: '2rem' }}>
      <div className="profile-hero" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '10px', marginBottom: '1.1rem' }}>
        <h2 style={{ margin: 0 }}>Feedback</h2>
        <p style={{ marginTop: '0.2rem', opacity: 0.85 }}>Share your thoughts and suggestions</p>
      </div>

      <div className="stat-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <Form>
          <Form.Group controlId="feedbackInput">
            <Form.Label style={{ fontWeight: 600, color: '#e5e7eb', marginBottom: '0.5rem' }}>Share your thoughts</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              style={{
                resize: 'vertical',
                backgroundColor: '#1f2937',
                color: '#f9fafb',
                border: '1px solid rgba(148,163,184,0.35)',
              }}
              disabled={feedbackSubmitting}
            />
            <Form.Text style={{ color: '#94a3b8', marginTop: '0.5rem', display: 'block' }}>
              Everyone can read shared feedback. Entries cannot be edited or removed once sent.
            </Form.Text>
          </Form.Group>
        </Form>
        {feedbackError && (
          <div style={{ color: '#f87171', marginTop: '0.75rem', fontSize: '0.9rem' }}>
            {feedbackError}
          </div>
        )}
        <Button
          variant="primary"
          onClick={handleSubmitFeedback}
          disabled={feedbackSubmitting || !feedbackText.trim()}
          style={{
            marginTop: '1rem',
            background: 'linear-gradient(135deg, #6366f1 0%, #22d3ee 100%)',
            border: 'none',
            padding: '0.5rem 1.5rem',
            fontWeight: 600,
          }}
        >
          {feedbackSubmitting ? 'Sending...' : 'Submit Feedback'}
        </Button>
      </div>

      <div className="stat-card" style={{ padding: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem 0', paddingBottom: '0.75rem', borderBottom: '2px solid rgba(255,255,255,0.15)', color: '#e5e7eb', fontSize: '1.1rem', fontWeight: 600, textAlign: 'left' }}>
          Feedback <span style={{ fontSize: '0.8rem', opacity: 0.65, fontStyle: 'normal', fontWeight: 400 }}>(most voted at top)</span>
        </h3>
        {feedbackLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Spinner animation="border" role="status" variant="light" size="sm" />
          </div>
        ) : feedbackItems.length === 0 ? (
          <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>No feedback has been shared yet.</div>
        ) : (
          <div
            style={{
              maxHeight: '600px',
              overflowY: 'auto',
              border: '1px solid rgba(148,163,184,0.25)',
              borderRadius: '10px',
              background: 'rgba(15,23,42,0.65)',
              padding: '0.75rem',
              boxShadow: 'inset 0 1px 3px rgba(15,23,42,0.35)',
            }}
          >
            {feedbackItems.map((item) => {
              const score = (item.upvotes ?? 0) - (item.downvotes ?? 0);
              const myVote = myVotes[item.id];
              const isVoting = votingId === item.id;
              return (
                <div
                  key={item.id}
                  style={{
                    padding: '0.65rem 0.5rem',
                    borderBottom: '1px solid rgba(148,163,184,0.18)',
                    backgroundColor: item.done ? 'rgba(34, 197, 94, 0.15)' : 'transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'background-color 0.3s'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#bfdbfe' }}>
                      {item.name || item.createdBy || 'Someone'}
                      {item.createdAt && (
                        <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: '0.4rem', fontSize: '0.82rem' }}>
                          {new Date(item.createdAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', color: '#e5e7eb', marginTop: '0.25rem' }}>
                      {item.content}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, gap: '2px', alignSelf: 'stretch' }}>
                    {!item.done && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleVote(item.id, 1)}
                          disabled={isVoting || !user?.username}
                          title="Upvote"
                          style={{
                            background: myVote === 1 ? 'rgba(34, 197, 94, 0.3)' : 'transparent',
                            border: '1px solid rgba(148,163,184,0.3)',
                            borderRadius: '4px',
                            color: myVote === 1 ? '#4ade80' : '#94a3b8',
                            cursor: user?.username && !isVoting ? 'pointer' : 'default',
                            padding: '2px 8px',
                            fontSize: '1rem',
                            lineHeight: 1,
                          }}
                        >
                          ▲
                        </button>
                        <span style={{ fontSize: '0.85rem', color: '#e5e7eb', minWidth: '1.5rem', textAlign: 'center' }}>
                          {score}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleVote(item.id, -1)}
                          disabled={isVoting || !user?.username}
                          title="Downvote"
                          style={{
                            background: myVote === -1 ? 'rgba(239, 68, 68, 0.3)' : 'transparent',
                            border: '1px solid rgba(148,163,184,0.3)',
                            borderRadius: '4px',
                            color: myVote === -1 ? '#f87171' : '#94a3b8',
                            cursor: user?.username && !isVoting ? 'pointer' : 'default',
                            padding: '2px 8px',
                            fontSize: '1rem',
                            lineHeight: 1,
                          }}
                        >
                          ▼
                        </button>
                      </>
                    )}
                    {item.done && !isMobile && (
                      <span style={{ color: '#4ade80', fontSize: '0.85rem', fontWeight: 600 }}>✓ Done</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Feedback;

